import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPlus, FiSend, FiPackage, FiCheck, FiClock,
  FiAlertCircle, FiEye, FiEdit, FiX, FiGrid, FiList, FiFileText, FiDownload,
  FiCalendar, FiDollarSign, FiColumns
} from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { useCompras } from '../../hooks/useCompras';
import { useDebounce } from '../../hooks/useDebounce';

const statusConfig = {
  DRAFT: { 
    label: 'Borrador', 
    color: 'gray', 
    icon: FiEdit,
    actions: ['edit', 'send', 'delete']
  },
  PENDING: { 
    label: 'Pendiente', 
    color: 'yellow', 
    icon: FiClock,
    actions: ['edit', 'send', 'cancel']
  },
  SENT: { 
    label: 'Enviada', 
    color: 'blue', 
    icon: FiSend,
    actions: ['view', 'approve', 'cancel']
  },
  CONFIRMED: { 
    label: 'Confirmada', 
    color: 'green', 
    icon: FiCheck,
    actions: ['view', 'receive']
  },
  PARTIAL_RECEIVED: { 
    label: 'Parcialmente Recibida', 
    color: 'orange', 
    icon: FiPackage,
    actions: ['view', 'receive']
  },
  RECEIVED: { 
    label: 'Recibida', 
    color: 'green', 
    icon: FiCheck,
    actions: ['view']
  },
  CANCELLED: { 
    label: 'Cancelada', 
    color: 'red', 
    icon: FiX,
    actions: ['view']
  }
};

const priorityConfig = {
  LOW: { label: 'Baja', color: 'gray' },
  NORMAL: { label: 'Normal', color: 'blue' },
  HIGH: { label: 'Alta', color: 'orange' },
  URGENT: { label: 'Urgente', color: 'red' }
};

const Compras = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('grid');
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('todas');

  // Aplicar debounce al searchQuery local (espera 500ms después de que el usuario deje de escribir)
  const debouncedSearchQuery = useDebounce(localSearchQuery, 500);
  
  const {
    ordenes,
    isLoading,
    error,
    searchQuery,
    statusFilter,
    buscarOrdenes,
    setStatusFilter,
    setProveedorFilter: setProveedorFilterHook,
    enviarOrden,
    aprobarOrden,
    cancelarOrden,
    getEstadisticas,
    clearError,
    limpiarBusqueda
  } = useCompras();

  // Efecto para búsqueda con debounce
  useEffect(() => {
    buscarOrdenes(debouncedSearchQuery);
  }, [debouncedSearchQuery, buscarOrdenes]);

  // Efecto para cambiar de tab
  useEffect(() => {
    if (activeTab === 'todas') {
      setStatusFilter('');
    } else if (activeTab === 'borradores') {
      setStatusFilter('DRAFT');
    } else if (activeTab === 'enviadas') {
      setStatusFilter('SENT');
    } else if (activeTab === 'confirmadas') {
      setStatusFilter('CONFIRMED');
    } else if (activeTab === 'recibidas') {
      setStatusFilter('RECEIVED');
    }
  }, [activeTab, setStatusFilter]);

  const stats = getEstadisticas();

  const getOrdenesFiltradas = () => {
    if (activeTab === 'todas') return ordenes;
    if (activeTab === 'borradores') return ordenes.filter(o => o.status === 'DRAFT' || o.status === 'PENDING');
    if (activeTab === 'enviadas') return ordenes.filter(o => o.status === 'SENT');
    if (activeTab === 'confirmadas') return ordenes.filter(o => o.status === 'CONFIRMED');
    if (activeTab === 'recibidas') return ordenes.filter(o => o.status === 'RECEIVED' || o.status === 'PARTIAL_RECEIVED');
    return ordenes;
  };

  const ordenesFiltradas = getOrdenesFiltradas();

  const getTotalOrdenes = (tipo) => {
    switch (tipo) {
      case 'borradores':
        return ordenes.filter(o => o.status === 'DRAFT' || o.status === 'PENDING').length;
      case 'enviadas':
        return ordenes.filter(o => o.status === 'SENT').length;
      case 'confirmadas':
        return ordenes.filter(o => o.status === 'CONFIRMED').length;
      case 'recibidas':
        return ordenes.filter(o => o.status === 'RECEIVED' || o.status === 'PARTIAL_RECEIVED').length;
      default:
        return ordenes.length;
    }
  };

  const getTotalMonto = (tipo) => {
    let ordenesFiltradas = ordenes;
    switch (tipo) {
      case 'borradores':
        ordenesFiltradas = ordenes.filter(o => o.status === 'DRAFT' || o.status === 'PENDING');
        break;
      case 'enviadas':
        ordenesFiltradas = ordenes.filter(o => o.status === 'SENT');
        break;
      case 'confirmadas':
        ordenesFiltradas = ordenes.filter(o => o.status === 'CONFIRMED');
        break;
      case 'recibidas':
        ordenesFiltradas = ordenes.filter(o => o.status === 'RECEIVED' || o.status === 'PARTIAL_RECEIVED');
        break;
      default:
        ordenesFiltradas = ordenes;
    }
    return ordenesFiltradas.reduce((sum, o) => sum + (o.total_amount || 0), 0);
  };

  const handleCreateNew = () => {
    navigate('/system/compras/nueva');
  };

  const handleDownload = () => {
    console.log('Descargar lista de órdenes de compra');
  };

  const handleClearSearch = () => {
    setLocalSearchQuery('');
    limpiarBusqueda();
  };

  const handleSearchChange = (e) => {
    setLocalSearchQuery(e.target.value);
  };

  const handleProveedorFilterChange = (value) => {
    setProveedorFilterHook(value);
  };

  // Limpiar errores
  const handleClearError = () => {
    clearError();
  };

  const handleViewOrder = (order) => {
    navigate(`/system/compras/${order.id}`);
  };

  const handleEditOrder = (order) => {
    navigate(`/system/compras/${order.id}/editar`);
  };

  const handleReceiveOrder = (order) => {
    navigate(`/system/compras/${order.id}/recibir`);
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const getThumbnailType = (status) => {
    // Todos los thumbnails en morado corporativo
    return 'blue-dark-gradient';
  };

  const getStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig.DRAFT;

    const badgeClass = {
      'gray': 'status-badge--secondary',
      'yellow': 'status-badge--warning',
      'blue': 'status-badge--info',
      'green': 'status-badge--success',
      'orange': 'status-badge--warning',
      'red': 'status-badge--danger'
    };

    return (
      <span className={`status-badge ${badgeClass[config.color] || 'status-badge--info'}`}>
        {config.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = priorityConfig[priority] || priorityConfig.NORMAL;
    
    return (
      <span className={`priority-badge priority-badge--${config.color}`}>
        {config.label}
      </span>
    );
  };

  const renderOrderActions = (order) => {
    const config = statusConfig[order.status] || statusConfig.DRAFT;
    
    return (
      <div className="content-card__actions">
        {config.actions.includes('view') && (
          <button 
            className="btn btn--ghost btn--sm"
            onClick={() => handleViewOrder(order)}
            title="Ver orden"
          >
            <FiEye />
          </button>
        )}
        
        {config.actions.includes('edit') && (
          <button 
            className="btn btn--secondary btn--sm"
            onClick={() => handleEditOrder(order)}
            title="Editar orden"
          >
            <FiEdit />
          </button>
        )}
        
        {config.actions.includes('send') && (
          <button 
            className="btn btn--primary btn--sm"
            onClick={() => enviarOrden(order.id)}
            title="Enviar orden"
          >
            <FiSend />
          </button>
        )}
        
        {config.actions.includes('approve') && (
          <button 
            className="btn btn--success btn--sm"
            onClick={() => aprobarOrden(order.id)}
            title="Aprobar orden"
          >
            <FiCheck />
          </button>
        )}
        
        {config.actions.includes('receive') && (
          <button 
            className="btn btn--info btn--sm"
            onClick={() => handleReceiveOrder(order)}
            title="Recibir productos"
          >
            <FiPackage />
          </button>
        )}
        
        {config.actions.includes('cancel') && (
          <button 
            className="btn btn--danger btn--sm"
            onClick={() => cancelarOrden(order.id)}
            title="Cancelar orden"
          >
            <FiX />
          </button>
        )}
      </div>
    );
  };

  if (isLoading) {
    return <LoadingGrid message="Cargando órdenes de compra..." />;
  }

  return (
    <div className="compras-page">
      {/* Error Alert */}
      {error && (
        <div className="alert alert--danger">
          <FiAlertCircle className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error}
            <button 
              className="btn btn--ghost btn--sm" 
              onClick={handleClearError}
              style={{ marginLeft: '10px' }}
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {/* Título */}
      <h1 className="page-title">ÓRDENES DE COMPRA</h1>

      {/* Menú de acciones */}
      <div className="page-header-with-menu">
        <div className="action-menu">
          <button 
            className="menu-item menu-item--first" 
            onClick={handleCreateNew}
          >
            + NUEVA ORDEN
          </button>
          
          <div className="menu-item menu-item--search">
            <label className="search-label">BUSCAR:</label>
            <input
              type="text"
              className="search-input"
              placeholder="N° de Orden"
              value={localSearchQuery}
              onChange={handleSearchChange}
            />
          </div>
          
          <div className="menu-item menu-item--filter">
            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">Todos los Estados</option>
              {Object.entries(statusConfig).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
          </div>

          <button className="menu-item menu-item--download" onClick={handleDownload}>
            DESCARGAR LISTA ↓
          </button>

          <div className="menu-item menu-item--views">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Vista de cuadrícula"
            >
              <FiGrid />
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="Vista de lista"
            >
              <FiList />
            </button>
            <button
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Vista de tabla"
            >
              <FiColumns />
            </button>
          </div>
        </div>
      </div>

      {/* Tabs de filtro */}
      <div className="filter-tabs">
        <button
          className={`filter-tab ${activeTab === 'todas' ? 'active' : ''}`}
          onClick={() => setActiveTab('todas')}
        >
          <div className="tab-content">
            <span className="tab-number">{getTotalOrdenes('todas')}</span>
            <span className="tab-label">Todas</span>
          </div>
          <div className="tab-amount">{formatCurrency(getTotalMonto('todas'))}</div>
        </button>

        <button
          className={`filter-tab ${activeTab === 'borradores' ? 'active' : ''}`}
          onClick={() => setActiveTab('borradores')}
        >
          <div className="tab-content">
            <span className="tab-number">{getTotalOrdenes('borradores')}</span>
            <span className="tab-label">Borradores</span>
          </div>
          <div className="tab-amount">{formatCurrency(getTotalMonto('borradores'))}</div>
        </button>

        <button
          className={`filter-tab ${activeTab === 'enviadas' ? 'active' : ''}`}
          onClick={() => setActiveTab('enviadas')}
        >
          <div className="tab-content">
            <span className="tab-number">{getTotalOrdenes('enviadas')}</span>
            <span className="tab-label">Enviadas</span>
          </div>
          <div className="tab-amount">{formatCurrency(getTotalMonto('enviadas'))}</div>
        </button>

        <button
          className={`filter-tab ${activeTab === 'confirmadas' ? 'active' : ''}`}
          onClick={() => setActiveTab('confirmadas')}
        >
          <div className="tab-content">
            <span className="tab-number">{getTotalOrdenes('confirmadas')}</span>
            <span className="tab-label">Confirmadas</span>
          </div>
          <div className="tab-amount">{formatCurrency(getTotalMonto('confirmadas'))}</div>
        </button>

        <button
          className={`filter-tab ${activeTab === 'recibidas' ? 'active' : ''}`}
          onClick={() => setActiveTab('recibidas')}
        >
          <div className="tab-content">
            <span className="tab-number">{getTotalOrdenes('recibidas')}</span>
            <span className="tab-label">Recibidas</span>
          </div>
          <div className="tab-amount">{formatCurrency(getTotalMonto('recibidas'))}</div>
        </button>
      </div>

      {/* Contenido */}
      {ordenesFiltradas.length === 0 && !isLoading && !error ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FiFileText size={48} />
          </div>
          <h3 className="empty-state-title">
            {localSearchQuery ? 'No se encontraron órdenes de compra' : 'No hay órdenes de compra registradas'}
          </h3>
          <p className="empty-state-description">
            {localSearchQuery
              ? `No se encontraron órdenes que coincidan con "${localSearchQuery}"`
              : 'Comienza creando tu primera orden de compra'
            }
          </p>
          {localSearchQuery && (
            <p>
              <button
                className="btn btn--ghost"
                onClick={handleClearSearch}
              >
                Limpiar búsqueda
              </button>
            </p>
          )}
        </div>
      ) : viewMode === 'table' ? (
        <div className="table-container">
          <table className="data-table data-table--compras">
            <thead>
              <tr>
                <th>N° ORDEN</th>
                <th>PROVEEDOR</th>
                <th>FECHA</th>
                <th className="text-center">ESTADO</th>
                <th className="text-center">PRIORIDAD</th>
                <th className="text-center">MONTO TOTAL</th>
                <th className="text-center">ACCIONES</th>
              </tr>
            </thead>
            <tbody>
              {ordenesFiltradas.map((orden) => (
                <tr
                  key={orden.id}
                  onClick={() => handleViewOrder(orden)}
                  style={{ cursor: 'pointer' }}
                >
                  <td><strong>{orden.order_number || `ORD-${orden.id}`}</strong></td>
                  <td>{orden.supplier_name || 'Sin proveedor'}</td>
                  <td>{formatDate(orden.order_date)}</td>
                  <td className="text-center">{getStatusBadge(orden.status)}</td>
                  <td className="text-center">{getPriorityBadge(orden.priority)}</td>
                  <td className="text-center">
                    <strong>{formatCurrency(orden.total_amount)}</strong>
                  </td>
                  <td className="text-center">
                    <div className="table-actions">
                      <button
                        className="btn btn--ghost btn--sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewOrder(orden);
                        }}
                        title="Ver orden"
                      >
                        <FiEye />
                      </button>
                      {statusConfig[orden.status]?.actions.includes('edit') && (
                        <button
                          className="btn btn--secondary btn--sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditOrder(orden);
                          }}
                          title="Editar"
                        >
                          <FiEdit />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? 'content-grid' : 'content-list'}>
          {ordenesFiltradas.map((orden) => (
            <div
              key={orden.id}
              className={viewMode === 'grid' ? 'content-card' : 'content-card-list'}
              onClick={() => handleViewOrder(orden)}
              style={{ cursor: 'pointer' }}
            >
              {viewMode === 'grid' ? (
                <>
                  <div className={`content-thumbnail ${getThumbnailType(orden.status)}`}>
                    <span className="logo-text">
                      {orden.order_number || `ORD-${orden.id}`}
                      <br/>
                      {formatCurrency(orden.total_amount)}
                    </span>
                  </div>
                  <div className="content-info">
                    <div className="content-title">{orden.supplier_name || 'Sin proveedor'}</div>
                    <div className="content-subtitle">
                      {formatDate(orden.order_date)} • {statusConfig[orden.status]?.label || 'N/A'}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className={`content-thumbnail-small ${getThumbnailType(orden.status)}`}>
                    <FiFileText className="thumbnail-icon" />
                  </div>
                  <div className="content-info-list">
                    <div className="content-title">{orden.order_number || `ORD-${orden.id}`}</div>
                    <div className="content-subtitle">{orden.supplier_name || 'Sin proveedor'}</div>
                    <div className="content-meta">
                      <span className="meta-item">
                        <FiCalendar size={12} /> {formatDate(orden.order_date)}
                      </span>
                      <span className="meta-item">
                        <FiDollarSign size={12} /> {formatCurrency(orden.total_amount)}
                      </span>
                      {getStatusBadge(orden.status)}
                      {orden.priority !== 'NORMAL' && (
                        <span className="meta-item meta-item--warning">
                          {priorityConfig[orden.priority]?.label}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="content-actions-list">
                    <FiPackage className="card-icon" />
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Compras;
